import React, { useState } from "react";
import globalAxios from "axios";
import { toast } from "react-toastify";
import { FiUploadCloud } from "react-icons/fi";
import { Modal } from "./Modal";
import { LoadingSpinner } from "./LoadingSpinner";
import GatedButton from "./GatedButton";
import { lockMessageFrom } from "../Pages/Publication/publicationUtils";

type PublishProductionButtonProps = {
	/** The stable content id : POST /api/deployment/publish-content { id }. */
	contentId?: string | null;
	/** "compact" renders an icon-only button (list rows); default renders the labelled button. */
	variant?: "default" | "compact";
	disabled?: boolean;
	className?: string;
	/** Button text. Toolbars pass a short « Publier » : the confirm modal spells out the target anyway. */
	label?: React.ReactNode;
	/** Called after a successful publish so the caller can refresh its status data. */
	onPublished?: () => void;
	/** Mirrors the in-flight state so the caller can show « Publication… » on its pill. */
	onPublishingChange?: (publishing: boolean) => void;
	/** Jeton exigé pour cliquer. Par défaut le droit global de déploiement ; un appelant qui sait
	 *  que la personne détient le droit de promotion PROPRE AU TYPE passe celui-ci, sinon le
	 *  bouton serait grisé pour quelqu'un que le serveur accepterait. */
	token?: string;
	/**
	 * L'appel de publication, quand ce n'est pas `publish-content { id }` : un écran dont la mise
	 * en production a son propre point d'entrée (les données macroéconomiques) passe le sien. Il
	 * doit LEVER en cas d'échec (la modale montre alors l'erreur, comme pour un contenu) et porte
	 * lui-même son message de succès. Le reste, confirmation, verrou 409, état occupé, est commun.
	 */
	publishAction?: () => Promise<void>;
	/** Corps de la confirmation, quand « Cette version remplacera la version en ligne. » ne dit pas juste. */
	confirmMessage?: string;
};

/**
 * « Publier en production » : the ONE per-content production action. Réservé aux détenteurs de
 * feature:deployment:write : sans elle le bouton reste visible mais grisé-cliquable (GatedButton)
 * et le clic explique la permission manquante : le serveur renvoie de toute façon 403.
 * Always goes through a confirmation modal (this version replaces the live one), then the
 * canonical preview→production chain server-side. Errors stay in the modal: a 409 shows who
 * holds the publication lock, a 404 says the content is gone; success closes and toasts.
 */
const PublishProductionButton: React.FC<PublishProductionButtonProps> = ({
	contentId,
	variant = "default",
	disabled,
	className,
	label = "Publier en production",
	onPublished,
	onPublishingChange,
	token = "feature:deployment:write",
	publishAction,
	confirmMessage = "Cette version remplacera la version en ligne."
}) => {
	const [showConfirm, setShowConfirm] = useState(false);
	const [publishing, setPublishing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const setBusy = (busy: boolean) => {
		setPublishing(busy);
		onPublishingChange?.(busy);
	};

	const publish = async () => {
		if (!contentId && !publishAction) return;
		setBusy(true);
		setError(null);
		try {
			if (publishAction) {
				await publishAction();
			} else {
				const res = await globalAxios.post(
					`/api/deployment/publish-content`,
					{ id: contentId },
					{ withCredentials: true, headers: { Accept: "application/json" } }
				);
				const d = res.data ?? {};
				const failed = Number(d.failed ?? 0);
				if (failed > 0) {
					const firstError = Array.isArray(d.items) ? d.items.find((i: any) => i?.error)?.error : null;
					setError(firstError ? `La publication a échoué : ${firstError}` : "La publication a échoué.");
					return;
				}
				toast.success("Publié en production ✓");
			}
			setShowConfirm(false);
			onPublished?.();
		} catch (err: any) {
			console.error(err);
			const status = err?.response?.status;
			if (status === 409) setError(lockMessageFrom(err));
			else if (status === 404) setError("Contenu introuvable.");
			else setError("La publication a échoué. Réessayez ou consultez les journaux.");
		} finally {
			setBusy(false);
		}
	};

	const openConfirm = (e: React.MouseEvent) => {
		// List rows navigate on click : the publish button must never trigger that.
		e.stopPropagation();
		setError(null);
		setShowConfirm(true);
	};

	const isDisabled = disabled || (!contentId && !publishAction);

	return (
		<>
			{variant === "compact" ? (
				<GatedButton
					token={token}
					onClick={openConfirm}
					disabled={isDisabled}
					title="Publier en production"
					aria-label="Publier en production"
					className={
						className ??
						"inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-[#005CA9]/40 hover:text-[#005CA9] disabled:cursor-not-allowed disabled:opacity-40"
					}
				>
					<FiUploadCloud className="h-3.5 w-3.5" />
				</GatedButton>
			) : (
				<GatedButton
					token={token}
					onClick={openConfirm}
					disabled={isDisabled}
					className={
						className ??
						"inline-flex items-center gap-2 rounded-lg bg-[#005CA9] px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004b8a] disabled:cursor-not-allowed disabled:bg-slate-300"
					}
				>
					<FiUploadCloud className="shrink-0" /> {label}
				</GatedButton>
			)}

			<Modal show={showConfirm} onHide={() => (publishing ? undefined : setShowConfirm(false))} size="sm" centered>
				<Modal.Header closeButton>
					<Modal.Title>Publier en production ?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700">{confirmMessage}</p>
					{error && (
						<p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
							{error}
						</p>
					)}
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setShowConfirm(false)}
						disabled={publishing}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={publish}
						disabled={publishing}
						className="inline-flex items-center gap-2 rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004b8a] disabled:cursor-not-allowed disabled:opacity-60"
					>
						{publishing ? <LoadingSpinner size="sm" inline /> : <FiUploadCloud className="h-4 w-4" />}
						{publishing ? "Publication…" : "Publier"}
					</button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default PublishProductionButton;
